<script setup lang="ts">
import {
  DTextField,
  PlusIcon,
  Trash03Icon,
  SearchMdIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Edit03Icon,
  XIcon,
  CheckIcon
} from 'front-library'
import { ref, watch } from 'vue'
import DynamicFormulatorField from './DynamicFormulatorField.vue'
import Popper from 'vue3-popper'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
interface validation {
  description: string
  criteria: string
  result: string
}

const props = withDefaults(
  defineProps<{
    modelValue: validation[]
    label: string
    disabled?: boolean
    error: string
    required?: boolean
  }>(),
  {
    modelValue: () => [],
    label: '',
    disabled: false,
    error: '',
    required: false
  }
)

const emit = defineEmits(['update:modelValue'])

const validations = ref(props.modelValue)
const oldDescription = ref('')
const newDescription = ref('')
// keys components
const codeEditorView = ref(0)

const editorShowIndex = ref(-1)

const emitUpdate = () => {
  console.log('emitUpdate')
  emit('update:modelValue', validations.value)
}

/**
 * Agregar nueva validacion
 */
const addValidation = () => {
  validations.value.push({
    description: t('components.validations.noTitle'),
    criteria: '',
    result: ''
  })
  emitUpdate()
}

/**
 * Eliminar validacion
 */
const deleteValidation = (index: number) => {
  validations.value.splice(index, 1)
  emitUpdate()
}

/**
 * Mover arriba/abajo la validacion seleccionada
 */
const moveValidation = (index: number, move: string) => {
  let fromIndex = 0
  let toIndex = 0
  if (move === 'up') {
    fromIndex = index
    toIndex = index - 1
  } else if (move === 'down') {
    fromIndex = index
    toIndex = index + 1
  }
  const validation = validations.value.splice(fromIndex, 1)[0]
  validations.value.splice(toIndex, 0, validation)
  codeEditorView.value += 1
  emitUpdate()
}

/**
 * Cambiar descripcion de la validacion
 */
const validationDescription = (domElement?: any, index: number = 0) => {
  const e = document.getElementsByClassName('textDescription')
  const f = document.getElementsByClassName('fieldDescription')
  if (domElement) {
    validations.value[index].description =
      oldDescription.value === ''
        ? validations.value[index].description
        : oldDescription.value
    domElement
      .getElementsByClassName('textDescription')[0]
      .classList.remove('hidden')
    domElement
      .getElementsByClassName('fieldDescription')[0]
      .classList.add('hidden')
    domElement
      .getElementsByClassName('fieldDescription')[0]
      .classList.remove('flex')
    emitUpdate()
  } else {
    for (let i = 0; i < e.length; i++) {
      if (e[i].className.indexOf('hidden') > -1) {
        validations.value[i].description = oldDescription.value
        emitUpdate()
        e[i].classList.remove('hidden')
        f[i].classList.add('hidden')
        f[i].classList.remove('flex')
      }
    }
  }
}
const editDescriptions = (
  e: any,
  index: number,
  editOpen: boolean,
  editSave?: boolean
) => {
  const parent = editOpen
    ? e.currentTarget.parentNode.parentNode.parentNode
    : e.currentTarget.parentNode.parentNode.parentNode.parentNode
  const input = parent.getElementsByClassName('fieldDescription')
  const description = parent.getElementsByClassName('textDescription')

  if (editOpen) {
    validationDescription()
    oldDescription.value = description[0].querySelector('p').innerHTML
    newDescription.value = oldDescription.value
    description[0].classList.add('hidden')
    input[0].classList.remove('hidden')
    input[0].classList.add('flex')
  } else {
    if (editSave) {
      validations.value[index].description = newDescription.value
    } else {
      validations.value[index].description = oldDescription.value
    }
    oldDescription.value = ''
    newDescription.value = ''
    description[0].classList.remove('hidden')
    input[0].classList.add('hidden')
    input[0].classList.remove('flex')
    emitUpdate()
  }
}

/**
 * Mostrar/ocultar desplegable de la validacion
 */
const dropdownFormulations = (e: any, i: number) => {
  editorShowIndex.value = i
  const parents = e.currentTarget.parentNode.parentNode.parentNode.parentNode
  const parentItem =
    parents.parentNode.parentNode.getElementsByClassName('drop_container')
  const iconSearch = parents.getElementsByClassName('searchIcon')
  const iconX = parents.getElementsByClassName('xIcon')
  const iconEdit = parents.parentNode.getElementsByClassName('editIcon')

  if (parentItem[0].classList.contains('hide')) {
    parentItem[0].classList.remove('hide')
    parentItem[0].classList.add('show')

    iconX[0].classList.remove('hidden')
    iconSearch[0].classList.add('hidden')

    if (!props.disabled) iconEdit[0].classList.remove('hidden')
  } else {
    parentItem[0].classList.remove('show')
    parentItem[0].classList.add('hide')

    iconSearch[0].classList.remove('hidden')
    iconX[0].classList.add('hidden')

    validationDescription(
      parents.parentNode.parentNode.getElementsByClassName('row')[0],
      i
    )
    if (!props.disabled) iconEdit[0].classList.add('hidden')
  }
}

// watch(
//   () => props.modelValue,
//   value => {
//     validations.value = Array.isArray(value) ? value : []
//   }
// )
</script>
<template>
  <div>
    <div class="flex justify-between items-center overflow-hidden pb-3">
      <p class="text-sm font-medium text-gray-800 py-2">
        {{ label }} <span v-if="required" class="text-red-500">*</span>
      </p>
      <Popper
        arrow
        offsetDistance="13"
        :content="t('components.validations.createValidation')"
        :hover="true"
        placement="top"
        class="tooltip">
        <div
          v-if="!disabled"
          class="p-2.5 bg-blue-100 rounded-md cursor-pointer"
          @click="addValidation()">
          <PlusIcon class="h-5 w-5 text-blue-600" />
        </div>
      </Popper>
    </div>
    <div class="validations" v-if="validations.length > 0">
      <div
        v-for="(validation, index) in validations"
        :key="index"
        class="py-2 px-3 mb-3 shadow-md rounded-xl border-t border-x border-x-gray-50 border-t-gray-50">
        <div class="flex justify-between items-center row gap-7">
          <div class="flex items-center textDescription gap-4">
            <p class="font-semibold text-gray-600 text-sm">
              {{ validation.description }}
            </p>
            <div
              v-if="!disabled"
              class="hidden editIcon cursor-pointer"
              @click="editDescriptions($event, index, true)">
              <Popper
                arrow
                offsetDistance="13"
                :content="t('components.validations.editDescription')"
                :hover="true"
                placement="top"
                class="tooltip">
                <Edit03Icon class="mt-1.5 h-5 w-5 text-gray-700" />
              </Popper>
            </div>
          </div>
          <div class="hidden fieldDescription items-center gap-2">
            <DTextField v-model="newDescription" :maxlength="50" />
            <Popper
              arrow
              offsetDistance="13"
              :content="t('form.button.confirm')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="p-2 rounded-md cursor-pointer bg-green-100 text-green-600"
                @click="editDescriptions($event, index, false, true)">
                <CheckIcon class="h-5 w-5" />
              </div>
            </Popper>
            <Popper
              arrow
              offsetDistance="13"
              :content="t('form.button.cancel')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="p-2 rounded-md cursor-pointer bg-red-100 text-red-600"
                @click="editDescriptions($event, index, false)">
                <XIcon class="h-5 w-5" />
              </div>
            </Popper>
          </div>
          <div
            :class="[disabled ? '' : 'gap-2']"
            class="flex justify-between items-center">
            <div class="searchIcon">
              <Popper
                arrow
                offsetDistance="13"
                :content="t('form.button.watch')"
                :hover="true"
                placement="top"
                class="tooltip">
                <div
                  class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
                  @click="dropdownFormulations($event, index)">
                  <SearchMdIcon class="h-5 w-5" />
                </div>
              </Popper>
            </div>
            <div class="xIcon hidden">
              <Popper
                arrow
                offsetDistance="13"
                :content="t('form.button.disguise')"
                :hover="true"
                placement="top"
                class="tooltip">
                <div
                  class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
                  @click="dropdownFormulations($event, index)">
                  <XIcon class="h-5 w-5" />
                </div>
              </Popper>
            </div>
            <div>
              <Popper
                arrow
                offsetDistance="13"
                :content="t('components.validations.uploadValidation')"
                :hover="true"
                placement="top"
                class="tooltip"
                :disabled="index === 0">
                <div
                  v-if="!disabled"
                  :class="[
                    index === 0
                      ? 'cursor-no-drop bg-gray-200 text-gray-700'
                      : 'cursor-pointer bg-gray-100 text-gray-600'
                  ]"
                  class="p-2 rounded-md">
                  <div @click="moveValidation(index, 'up')">
                    <ChevronUpIcon class="h-5 w-5" />
                  </div>
                </div>
              </Popper>
            </div>
            <div>
              <Popper
                arrow
                offsetDistance="13"
                :content="t('components.validations.lowerValidation')"
                :hover="true"
                placement="top"
                class="tooltip"
                :disabled="index === validations.length - 1">
                <div
                  v-if="!disabled"
                  :class="[
                    index === validations.length - 1
                      ? 'cursor-no-drop  bg-gray-200 text-gray-700'
                      : 'cursor-pointer bg-gray-100 text-gray-600'
                  ]"
                  class="p-2 rounded-md">
                  <div @click="moveValidation(index, 'down')">
                    <ChevronDownIcon class="h-5 w-5" />
                  </div>
                </div>
              </Popper>
            </div>
            <div>
              <Popper
                arrow
                offsetDistance="13"
                :content="t('table.action.remove')"
                :hover="true"
                placement="top"
                class="tooltip">
                <div
                  v-if="!disabled"
                  class="p-2 rounded-md cursor-pointer bg-gray-100 text-gray-600"
                  @click="deleteValidation(index)">
                  <Trash03Icon class="h-5 w-5" />
                </div>
              </Popper>
            </div>
          </div>
        </div>

        <div class="drop_container hide">
          <div v-if="editorShowIndex === index" class="item p-3">
            <DynamicFormulatorField
              :key="codeEditorView"
              :disabled="disabled"
              :label="t('components.validations.criterion')"
              v-model="validation.criteria"
              @update:modelValue="emitUpdate()" />
            <DynamicFormulatorField
              :key="codeEditorView"
              :disabled="disabled"
              class="pt-3"
              :label="t('components.validations.result')"
              v-model="validation.result"
              @update:modelValue="emitUpdate()" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="rounded-md p-3 bg-gray-50 text-gray-600"
      :class="[
        error === ''
          ? 'text-center bg-gray-50 text-gray-600'
          : 'border border-red-500'
      ]">
      <p>{{ t('components.validations.noValidationsCreated') }}</p>
    </div>
    <span class="text-xs text-red-500">{{ error }}</span>
  </div>
</template>
<style scoped>
.drop_container {
  overflow: hidden;
  max-height: 0;
}

.drop_container.show {
  max-height: 403px;
  transition: max-height 0.5s ease-in;
}

.drop_container.hide {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.5s ease-out;
}
.drop_container > .item {
  display: flex;
  flex-direction: column;
}
</style>
